import { createRef } from "react";

import type { IUserCommunicationTemplate } from "@/src/common/UserCommunicationTemplate";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import type { ILink } from "@Components/Link";
import { Loading } from "@Components/Loading";
import { Ajax, AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType, clearError } from "@Components/Modules/ErrorHandler";
import { capitalize } from "@Components/Modules/Helpers";
import { isFirstLastNameValid } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";

import type { IAccountNumber } from "../common/AccountNumber";
import { AccountNumber } from "../common/AccountNumber";
import type { IButtonsBar } from "../common/ButtonsBar";
import { ButtonsBar } from "../common/ButtonsBar";
import type { ICreditCardInfo } from "../common/CreditCardInfo";
import { CreditCardInfo } from "../common/CreditCardInfo";
import type { IInformationContent } from "../common/InformationContent";
import { InformationContent } from "../common/InformationContent";
import type { INavigationModalActions } from "../common/NavigationModal";
import type { IInputTracker } from "../telemetry/InputTracker";
import { SetInputTrackerProps } from "../telemetry/InputTracker";
import type { ICashbackAdBanner, ICashbackBannerElement } from "./CashbackCommonInterfaces";

import * as css from "./CashbackWithdrawForm.module.css";

export interface ICashbackWithdrawForm
    extends INavigationModalActions<ICashbackAdBanner>, IComponentAjax<IUserCommunicationTemplate> {
    firstLastNameTextbox: IInputTracker<ITextbox> | null;
    accountNumber: IAccountNumber | null;
    settingWithdrawDataLink?: ILink;
    amountWarning?: IInformationContent;
    withdrawDataAjaxAction?: string;
    buttonsBar: IButtonsBar | null;
    firstLastNameInvalidError?: IValidator;
    accountNumberInvalidError?: IValidator;
    bankInfoInvalidError?: IValidator;
    cashbackWithdrawFormTitle: string;
    cashbackWithdrawFormAmountLabel: string;
    cashbackWithdrawFormFirstLastNameLabel: string;
    cashbackWithdrawFormFirstLastNameWatermarkText: string;
    cashbackWithdrawFormAccountNumberWatermarkText: string;
}

export interface ICashbackWithdrawSuccessPage {
    title: string;
    description: string;
}

interface IWithdrawalDataResult {
    withdrawalAccountNumber?: string;
    withdrawalRecipient: string;
    withdrawalAmount: string;
    bankName: string;
    withdrawalToken: string;
    showAmountWarning: boolean;
    sponsoredBanner: ICashbackBannerElement;
    creditCardInfo: ICreditCardInfo;
    withdrawalInfoText: string;
    hasAlfaImpresjaCard: boolean;
}

interface ICashbackWithdrawFormState extends IComponentAjaxState {
    withdrawalAccountNumber: string;
    withdrawalRecipient: string;
    withdrawalAmount: string;
    bankName: string;
    withdrawalToken: string;
    validBankInfo: boolean;
    savedBankInfo: boolean;
    showAmountWarning: boolean;
    skipValidation: boolean;
    creditCardInfo: ICreditCardInfo | null;
    withdrawalInfoText: string;
    hasAlfaImpresjaCard: boolean;
}

export class CashbackWithdrawForm extends ComponentAjax<
    IUserCommunicationTemplate,
    ICashbackWithdrawForm,
    ICashbackWithdrawFormState
> {
    private get renderFirstLastName() {
        if (!this.props.firstLastNameTextbox) {
            return null;
        }

        const firstLastName = SetInputTrackerProps<ITextbox>({
            ...this.props.firstLastNameTextbox,
            focusTelemetryCustom: {
                eventName: "cshbck-wthdrAddresseeEdit",
                actionType: "Undefined",
            },
            properties: {
                ...this.props.firstLastNameTextbox.properties,
                maxLength: 90,
                watermarkText: this.props.cashbackWithdrawFormFirstLastNameWatermarkText,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    this.setState({
                        withdrawalRecipient: capitalize(event.target.value),
                    }),
                value: this.state.withdrawalRecipient,
                isReadOnly: this.state.savedBankInfo,
            },
        });

        return (
            <div className={css.formRows}>
                <span>{this.props.cashbackWithdrawFormFirstLastNameLabel}</span>
                <div>
                    <Textbox {...firstLastName} ref={this.firstLastNameTextboxRef} />
                    <ErrorContainer {...this.errors.firstLastNameInvalidError.container} />
                    <ErrorContainer {...this.errors.firstLastNameInvalidCharsError.container} />
                </div>
            </div>
        );
    }

    private get renderAccountNumber() {
        if (!this.props.accountNumber) {
            return null;
        }

        const account: IAccountNumber = {
            ...this.props.accountNumber,
            accountNumberTextbox: {
                ...this.props.accountNumber.accountNumberTextbox,
                focusTelemetryCustom: { eventName: "cshbck-wthdrAccountEdit", actionType: "Undefined" },
                properties: {
                    ...this.props.accountNumber.accountNumberTextbox.properties,
                    maxLength: 32,
                    watermarkText: this.props.cashbackWithdrawFormAccountNumberWatermarkText,
                    value: this.state.withdrawalAccountNumber,
                    isReadOnly: this.state.savedBankInfo,
                },
            },
        };

        return (
            <div className={`${css.formRows} ${css.accountNumberForm}`}>
                {!this.state.hasAlfaImpresjaCard && (
                    <>
                        <AccountNumber
                            {...account}
                            ref={this.accountNumberTextboxRef}
                            bankName={this.state.bankName}
                            onChange={this.changeAccountNumber}
                        />
                        <ErrorContainer {...this.errors.accountNumberInvalidError.container} />
                        <ErrorContainer {...this.errors.bankInfoInvalidError.container} />
                    </>
                )}
                {this.state.creditCardInfo && <CreditCardInfo {...this.state.creditCardInfo} />}
            </div>
        );
    }

    private get buttonsBar() {
        if (!this.props.buttonsBar) {
            return null;
        }
        const buttonsBar: IButtonsBar = {
            ...this.props.buttonsBar,

            nextButton: {
                ...this.props.buttonsBar.nextButton,
                onClick: this.submitForm,
            },
            cancelButton: {
                ...this.props.buttonsBar.cancelButton,
                onClick: this.previousPage,
            },
        };

        return <ButtonsBar {...buttonsBar} />;
    }

    private get getForm() {
        if (this.state.isLoading) {
            return null;
        }

        return (
            <>
                <div className={css.contentForm}>
                    <h1 className={css.standardModalTitle}>{this.props.cashbackWithdrawFormTitle}</h1>
                    <div className={css.amount}>
                        {this.props.cashbackWithdrawFormAmountLabel}
                        <span>{this.state.withdrawalAmount}</span>
                    </div>

                    <div className={css.formInputs}>
                        {this.renderFirstLastName}
                        {this.renderAccountNumber}
                    </div>
                </div>
                {this.state.withdrawalInfoText && (
                    <div className={css.withdrawalInfoText}>
                        <span>*</span>
                        <span
                            className={css.text}
                            dangerouslySetInnerHTML={{ __html: this.state.withdrawalInfoText }}
                        />
                    </div>
                )}
                {this.buttonsBar}
            </>
        );
    }

    private get getWarning() {
        if (this.state.isLoading) {
            return null;
        }

        const onClickButton = () => {
            this.setState({ showAmountWarning: false });
        };

        return <InformationContent {...this.props.amountWarning} onClickButton={onClickButton} />;
    }

    public static readonly defaultProps: ICashbackWithdrawForm = {
        ...ComponentAjax.defaultProps,
        firstLastNameTextbox: null,
        accountNumber: null,
        buttonsBar: null,
        cashbackWithdrawFormTitle: "",
        cashbackWithdrawFormAmountLabel: "",
        cashbackWithdrawFormFirstLastNameLabel: "",
        cashbackWithdrawFormFirstLastNameWatermarkText: "",
        cashbackWithdrawFormAccountNumberWatermarkText: "",
    };

    public firstLastNameTextboxRef = createRef<Textbox>();

    public accountNumberTextboxRef = createRef<AccountNumber>();

    public errors: IErrors = {
        default: {
            ...this.defaultErrors().default,
            type: ErrorType.Validator,
        },
        firstLastNameInvalidCharsError: {
            name: "firstLastName-invalid-chars",
            type: ErrorType.Validator,
            targetRefObj: this.firstLastNameTextboxRef,
            container: { ref: "firstLastName-invalid-chars-container" },
            onValidate: () => {
                if (!isFirstLastNameValid(this.state.withdrawalRecipient)) {
                    return false;
                }
                return true;
            },
        },
        firstLastNameInvalidError: {
            name: "firstLastName-invalid",
            type: ErrorType.Validator,
            targetRefObj: this.firstLastNameTextboxRef,
            container: { ref: "firstLastName-invalid-container" },
            onValidate: () => {
                if (this.state.skipValidation || this.state.savedBankInfo) {
                    return true;
                }

                if (!this.state.withdrawalRecipient) {
                    return false;
                }

                const values = this.state.withdrawalRecipient.split(" ").filter((v) => v !== "");

                if (values.length > 1) {
                    return true;
                }
                return this.state.withdrawalRecipient.trim().length === 0;
            },
        },
        bankInfoInvalidError: {
            name: "bank-info-invalid",
            type: ErrorType.Validator,
            targetRefObj: this.accountNumberTextboxRef,
            container: { ref: "account-number-invalid-container" },
            onValidate: () => {
                if (this.state.skipValidation || this.state.validBankInfo) {
                    return true;
                }

                return false;
            },
        },
        accountNumberInvalidError: {
            name: "account-number-invalid",
            type: ErrorType.Validator,
            targetRefObj: this.accountNumberTextboxRef,
            container: { ref: "account-number-invalid-container" },
            onValidate: () => {
                if (this.state.skipValidation || this.state.withdrawalAccountNumber?.length === 26) {
                    return true;
                }

                return false;
            },
        },
    };

    private isSubmitted: boolean = false;

    constructor(props: ICashbackWithdrawForm) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        this.state = {
            isLoading: true,
            withdrawalAccountNumber: "",
            withdrawalRecipient: "",
            withdrawalAmount: "",
            bankName: "",
            withdrawalToken: "",
            validBankInfo: false,
            savedBankInfo: false,
            showAmountWarning: false,
            creditCardInfo: null,
            withdrawalInfoText: "",
            skipValidation: true,
            hasAlfaImpresjaCard: false,
        };
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.cashbackWithdrawFormComponent)}>
                {this.state.showAmountWarning ? this.getWarning : this.getForm}
                {this.renderAjaxErrorContainer()}
                {this.state.isLoading && <Loading isModal={true} />}
            </div>
        );
    }

    public componentDidMount() {
        const ajax = new Ajax<IWithdrawalDataResult>({
            ajaxAction: this.props.withdrawDataAjaxAction,
        });

        ajax.success = (response: IWithdrawalDataResult) => {
            this.setState({
                withdrawalAccountNumber: response.withdrawalAccountNumber,
                withdrawalRecipient: response.withdrawalRecipient,
                withdrawalAmount: response.withdrawalAmount,
                bankName: response.bankName,
                withdrawalToken: response.withdrawalToken,
                validBankInfo: response.bankName !== null,
                savedBankInfo:
                    response.withdrawalAccountNumber?.length > 0 || response.hasAlfaImpresjaCard === true,
                showAmountWarning: response.showAmountWarning,
                creditCardInfo: response.creditCardInfo,
                withdrawalInfoText: response.withdrawalInfoText,
                skipValidation: false,
                isLoading: false,
                hasAlfaImpresjaCard: response.hasAlfaImpresjaCard,
            });

            if (this.props.saveValues) {
                this.props.saveValues({ banner: response.sponsoredBanner });
            }
        };

        ajax.request();
    }

    protected ajaxSuccess(response: IUserCommunicationTemplate): void {
        this.setState({ isLoading: false });

        if (response) {
            this.isSubmitted = true;

            if (this.props.saveValues) {
                this.props.saveValues({ userCommunicationTemplate: response });
            }

            if (this.props.onSubmit) {
                this.props.onSubmit();
            }
        }
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private submitForm = () => {
        if (this.isSubmitted) {
            return;
        }

        this.ajaxRequest({
            token: this.state.withdrawalToken,
            accountNumber: this.state.withdrawalAccountNumber,
            recipient: this.state.withdrawalRecipient,
        });
    };

    private previousPage = () => {
        if (this.props.previousPage) {
            this.props.previousPage.call(this);
        }
    };

    private changeAccountNumber = (accountNumberValue: string, validBankInfo: boolean): void => {
        this.setState({
            withdrawalAccountNumber: accountNumberValue.replaceAll(" ", ""),
            validBankInfo,
        });

        clearError(this, this.errors.accountNumberInvalidError);
        clearError(this, this.errors.bankInfoInvalidError);
    };
}
